import { skipToken, useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { LapCandidateFilters } from "@/types";

export const lapComparisonKeys = {
  all: ["lapComparison"] as const,
  comparison: (ref: string, cmp: string, channels: string[]) =>
    [...lapComparisonKeys.all, "compare", ref, cmp, channels] as const,
  candidates: (filters: LapCandidateFilters) =>
    [...lapComparisonKeys.all, "candidates", filters] as const,
};

/**
 * GPS-aligned comparison of two laps. Disabled until both slots are set;
 * the channel list is part of the key so switching channels refetches only
 * the gridded values (alignment itself is cheap server-side and the session
 * models are cached there).
 */
export function useLapComparison(
  ref: string | null,
  cmp: string | null,
  channels: string[],
) {
  return useQuery({
    queryKey: lapComparisonKeys.comparison(ref ?? "", cmp ?? "", channels),
    queryFn:
      ref && cmp
        ? () => apiClient.getLapComparison({ ref, cmp, channels })
        : skipToken,
    // Switching channels keeps the previous result of the SAME lap pair on
    // screen while the new one loads; a different pair starts clean so the
    // old laps are never shown under the new cards.
    placeholderData: (previous, previousQuery) =>
      previousQuery &&
      previousQuery.queryKey[2] === (ref ?? "") &&
      previousQuery.queryKey[3] === (cmp ?? "")
        ? previous
        : undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
}

/** Candidate laps for the picker; disabled until a track and scope are known. */
export function useLapCandidates(filters: LapCandidateFilters | null) {
  return useQuery({
    queryKey: lapComparisonKeys.candidates(
      filters ?? { trackId: "", scope: "mine" },
    ),
    queryFn: filters?.trackId
      ? () => apiClient.getLapCandidates(filters)
      : skipToken,
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
  });
}
